import accounting from "accounting";

// max allowed amount
const MAX_ALLOWED = 1000000000; // 1 billion
const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
const TEENS = ["ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
const SCALE = ["", " thousand", " million", " billion", " trillion"];

const twoDigitWords = (n) => {
  try {
    if (n < 10) return ONES[n];
    if (n < 20) return TEENS[n - 10];
    const t = Math.floor(n / 10);
    const o = n % 10;
    return TENS[t] + (o ? "-" + ONES[o] : "");
  } catch (err) {
    console.warn("twoDigitWords error", err);
    return "";
  }
};

const threeDigitsToWords = (num) => {
  try {
    const hundreds = Math.floor(num / 100);
    const rest = num % 100;
    const parts = [];
    if (hundreds) parts.push(ONES[hundreds] + " hundred");
    if (rest) parts.push(twoDigitWords(rest));
    return parts.join(" ").trim();
  } catch (err) {
    console.warn("threeDigitsToWords error", err);
    return "";
  }
};

const integerToWords = (n) => {
  try {
    if (n === 0) return "zero";
    let parts = [];
    let scaleIndex = 0;
    while (n > 0) {
      const chunk = n % 1000;
      if (chunk) {
        const chunkWords = threeDigitsToWords(chunk) + (SCALE[scaleIndex] || "");
        parts.unshift(chunkWords.trim());
      }
      n = Math.floor(n / 1000);
      scaleIndex++;
      if (scaleIndex >= SCALE.length && n > 0) return null; // too large
    }
    return parts.join(" ").replace(/\s+/g, " ").trim();
  } catch (err) {
    console.warn("integerToWords error", err);
    return null;
  }
};

const amountToWords = ({ amount, format = "full", centsAlreadyPresent = false } = {}) => {
  try {
    if (!isFinite(amount) || amount < 0) return null;
    // round to cents
    const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
    const dollars = Math.floor(rounded);
    const cents = Math.round((rounded - dollars) * 100);

    const dollarsWordsRaw = integerToWords(dollars);
    if (dollarsWordsRaw === null) return null;
    const dollarsWords = dollarsWordsRaw.charAt(0).toUpperCase() + dollarsWordsRaw.slice(1);
    const centsStr = String(cents).padStart(2, "0"); // Always have cents as 2-digit string
    switch (format) {
      case "fraction":
        // if document already has "/100 Dollars" (centsAlreadyPresent), only adding the numerator (e.g. "and 42")
        if (centsAlreadyPresent) {
          return (dollars === 0 ? "Zero" : dollarsWords) + " and " + centsStr;
        }
        return (dollars === 0 ? "Zero" : dollarsWords) + " and " + centsStr + "/100 dollars";

      case "no-dollars":
        // "Three hundred ... and 42/100" - just the fraction part
        return (dollars === 0 ? "Zero" : dollarsWords) + " and " + centsStr + "/100";

      case "full":
      default:
        // "Thirty-four dollars and 00 cents" — always appending "dollars" and always including cents as "NN cents"
        const dollarWord = dollars === 1 ? " dollar" : " dollars";
        return (dollars === 0 ? "Zero" : dollarsWords) + dollarWord + " and " + centsStr + " cents";
    }
  } catch (err) {
    console.warn("amountToWords error", err);
    return null;
  }
};

export const amountToWordsHelper = ({ event = {}, specificData }) => {
  try {
    const targetName = event.target.getAttribute("data-amount-words");
    const raw = (event.target.value || "").toString().trim();

    if (!raw) {
      document.querySelectorAll(`[name="${targetName}"]`).forEach((el) => {
        el.value = "";
        specificData[el.name] = "";
        el.dispatchEvent(new Event("input", { bubbles: true }));
      });
    } else {
      const num = accounting.unformat(raw) || 0;

      if (!isFinite(num) || num < 0) {
        document.querySelectorAll(`[name="${targetName}"]`).forEach((el) => {
          el.value = "";
          specificData[el.name] = "";
          el.dispatchEvent(new Event("input", { bubbles: true }));
        });
      } else if (num >= MAX_ALLOWED) {
        document.querySelectorAll(`[name="${targetName}"]`).forEach((el) => {
          el.value = "";
          specificData[el.name] = "";
          el.dispatchEvent(new Event("input", { bubbles: true }));
        });
      } else {
        // formatting numeric input and populating words for that number
        const formatted = accounting.formatNumber(num, 2);
        event.target.value = formatted;
        specificData[event.target.name] = formatted;
        if (event.target.parentNode && event.target.parentNode.classList.contains("grow-with-input")) {
          event.target.parentNode.dataset.replicatedValue = formatted;
        }
        const format = event.target.getAttribute("data-amount-words-format") || "full";
        const centsAlreadyPresent = event.target.hasAttribute("data-cents-already-present");
        const words = amountToWords({ amount: num, format, centsAlreadyPresent }) || "";
        document.querySelectorAll(`[name="${targetName}"]`).forEach((el) => {
          el.value = words;
          specificData[el.name] = words;
          el.dispatchEvent(new Event("input", { bubbles: true }));
        });
      }
    }
  } catch (err) {
    console.warn("amount-words conversion failed", err);
  }
};
